// lib/koi-pond-v3/shaders/koiMaterialV3.ts
//
// T8.2 — koi material. Three composited components per the brief:
//   1. DIFFUSE: moonlight-tinted base lit by a wrapped lambert so the
//      terminator is soft and milky (koi flesh isn't a hard-shaded
//      sphere). An ambient floor keeps shadowed flanks from going black.
//   2. FAKE SSS: back-lit translucency. Light arriving from behind the
//      fish toward the viewer glows THROUGH the body — strongest on the
//      thin parts (fins, tail) via the bodyFraction attribute. This is
//      what sells "bone-china seen through water" rather than solid
//      plastic.
//   3. FRESNEL RIM: a cool rim at grazing angles picks the silhouette
//      out against the near-black water — the koi reads as a lit edge
//      even when the body is in shadow.
// Plus a restrained pearlescent specular and optional per-variant
// markings (kohaku red on the back), and the shared depth-fog snippet so
// koi recede into the teal like every other v3 surface.
//
// Lighting is done in-shader from a uMoonDir uniform (NOT three's light
// system) so the material is self-contained and identical in the pond
// scene and the debug viewer. cameraPosition / model matrices are
// three.js ShaderMaterial built-ins.

import { ShaderMaterial, Color, Vector3, DoubleSide } from 'three';
import { palette, type PaletteKey } from '../palette';
import { DEPTH_FOG_UNIFORM_DECL, DEPTH_FOG_APPLY_SNIPPET } from './depthFog';
import {
  SWIM_UNIFORM_DECL,
  SWIM_VERTEX_SNIPPET,
  makeSwimUniforms,
  type SwimParams,
} from '../animation/koiSwim';

const KOI_VERT = `
  ${SWIM_UNIFORM_DECL}
  attribute float bodyFraction;
  attribute float partIndex;
  varying vec3 vWorldPos;
  varying vec3 vNormalW;
  varying vec2 vUv;
  varying float vBodyFraction;
  varying float vPartIndex;
  void main() {
    vUv = uv;
    vBodyFraction = bodyFraction;
    vPartIndex = partIndex;
    ${SWIM_VERTEX_SNIPPET}
    vec4 wp = modelMatrix * vec4(swimPos, 1.0);
    vWorldPos = wp.xyz;
    vNormalW = normalize(mat3(modelMatrix) * swimNrm);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(swimPos, 1.0);
  }
`;

const KOI_FRAG = `
  precision highp float;
  uniform vec3 uMoonDir;     // normalized direction TO the moon
  uniform vec3 uMoonColor;   // moonlight cream
  uniform vec3 uBaseColor;   // body diffuse (per-variant)
  uniform vec3 uPatternColor; // markings accent (per-variant)
  uniform float uPatternStrength; // 0 = none (ogon), up to ~1 (kohaku)
  uniform vec3 uRimColor;    // fresnel rim (cool for white koi, warm for ogon)
  uniform float uGroundCool; // 0 = warm moonlit (ogon), 1 = cool bone-china white
  uniform float uDepthShallow; // world y treated as "at the surface"
  uniform float uDepthDeep;    // world y treated as "deep/floor"
  uniform float uMoonDim;      // T10.3 cloud-dimming (1 = full, 0.7 = dimmed)
  uniform float uSSS;          // T12.5 — 1 = full subsurface scattering, 0 = off (mobile)
  uniform float uShimmer;      // dragon-gate dragon-scale glint (rest 0 = no-op)
  uniform float uTime;
  ${DEPTH_FOG_UNIFORM_DECL}
  varying vec3 vWorldPos;
  varying vec3 vNormalW;
  varying vec2 vUv;
  varying float vBodyFraction;
  varying float vPartIndex;

  // cheap value noise for the markings
  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float noise(vec2 p) {
    vec2 i = floor(p); vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  void main() {
    // Two-sided lighting: flip the normal toward the viewer so thin
    // fins (rendered DoubleSide) light correctly from either face.
    vec3 N = normalize(vNormalW);
    vec3 V = normalize(cameraPosition - vWorldPos);
    if (dot(N, V) < 0.0) N = -N;
    vec3 L = normalize(uMoonDir);

    // The light tint applied to the ground colour. Ogon (uGroundCool=0)
    // keeps the full warm moonlight; the white varieties pull toward a
    // cool near-white so the bone-china ground stays crisp against the
    // red markings instead of going amber.
    vec3 lightTint = mix(uMoonColor, vec3(0.90, 0.94, 1.0), uGroundCool * 0.75);

    // ── 1. Base color + per-variant markings ──────────────────────
    vec3 base = uBaseColor;
    // markings only on the BODY (partIndex 0), biased to the back
    // (uv v≈0.25 is the dorsal ridge of the swept ring).
    if (uPatternStrength > 0.001 && vPartIndex < 0.5) {
      // Bold kohaku/sanke patches: wide flank coverage, crisp near-solid
      // edges, and a vivid patch colour so the red reads strongly against the
      // white body instead of washing toward cream.
      float dorsalMask = smoothstep(0.56, 0.08, abs(vUv.y - 0.22));
      float blotch = noise(vec2(vUv.x * 5.0, vUv.y * 3.5));
      blotch += 0.5 * noise(vec2(vUv.x * 11.0, vUv.y * 8.0));
      // narrow smoothstep → crisp patch borders (not a soft gradient); lower
      // threshold → bigger patches covering more of the body.
      float mark = smoothstep(0.43, 0.49, blotch / 1.5) * dorsalMask;
      // patch colour pushed brighter/more saturated than the palette base so
      // the soft mix can't dilute it back to faint pink. NB: do not name this
      // 'patch' — that is a reserved GLSL word (tessellation) and the fragment
      // shader fails to compile, rendering the koi invisible.
      vec3 patchColor = uPatternColor * 1.28;
      base = mix(base, patchColor, clamp(mark * uPatternStrength * 1.35, 0.0, 1.0));
    }

    // ── 2. Diffuse (wrapped lambert, milky terminator) ────────────
    float ndl = dot(N, L);
    float wrap = 0.5;
    float diff = clamp((ndl + wrap) / (1.0 + wrap), 0.0, 1.0);
    vec3 diffuse = base * lightTint * (0.58 + 0.84 * diff);

    // ── 3. Fake SSS (back-lit translucency, thin parts glow more) ──
    float backLight = pow(clamp(dot(V, -L), 0.0, 1.0), 2.2);
    float thin = clamp(vBodyFraction, 0.0, 1.6) / 1.6; // tail/fin tips ≈ 1
    float sss = backLight * (0.22 + 0.78 * thin);
    // Round-2: warmer, stronger back-lit glow through the thin fins/tail so
    // their edges read as translucent membrane catching the moonlight.
    vec3 sssColor = mix(base, lightTint, 0.55) * sss * 1.45 * uSSS;

    // ── 4. Fresnel rim ────────────────────────────────────────────
    float fres = pow(1.0 - clamp(dot(N, V), 0.0, 1.0), 3.0);
    vec3 rim = uRimColor * fres * 1.1; // stronger silhouette catch so the eye finds the koi

    // ── 5. Pearlescent specular (restrained, faint shimmer) ───────
    vec3 H = normalize(L + V);
    float spec = pow(clamp(dot(N, H), 0.0, 1.0), 48.0);
    spec *= 0.25 + 0.08 * sin(uTime * 0.6 + vWorldPos.x * 3.0);
    vec3 specular = lightTint * spec;

    // ── 6. Ambient floor ─────────────────────────────────────────
    // A small view/light-independent fill so a koi angled fully away from
    // the moon (diffuse AND back-lit SSS both near zero) never drops below
    // a readable level. Without it the school flickers in and out of
    // legibility as it drifts (owner visual audit). Kept low so lit koi
    // still clearly out-read shadowed ones — it's a floor, not a wash.
    vec3 ambient = base * lightTint * 0.34;

    vec3 color = ambient + diffuse + sssColor + rim + specular;

    // Saturation lift — the koi are the scene's subject but read washed-out
    // cream against the cool caustics; push chroma so the kohaku red and the
    // gold ogon register as saturated koi (luma-preserving, so they don't
    // also get brighter here — brightness is handled by the terms above).
    float koiLuma = dot(color, vec3(0.299, 0.587, 0.114));
    color = mix(vec3(koiLuma), color, 1.28);

    // Dragon-scale shimmer (rest uShimmer=0): an iridescent hue sweep + boosted
    // fresnel running down the body — fires at the leap apex (the Dragon Gate
    // transformation tease).
    if (uShimmer > 0.001) {
      // Darken the body to a near-black SILHOUETTE during the leap so it reads as
      // a dragon crossing the bright moon; iridescence rides only a THIN fresnel
      // rim (owner note: full-body iridescence read as a rainbow blimp, wanted a
      // dark silhouette with just a rim).
      color *= (1.0 - 0.88 * uShimmer);
      float sweep = sin(vBodyFraction * 9.0 - uTime * 6.0) * 0.5 + 0.5;
      vec3 irid = vec3(
        0.5 + 0.5 * sin(vBodyFraction * 6.2831 + 0.0),
        0.5 + 0.5 * sin(vBodyFraction * 6.2831 + 2.094),
        0.5 + 0.5 * sin(vBodyFraction * 6.2831 + 4.188)
      );
      float rimEdge = pow(fres, 2.5);            // thin rim only
      float glint = uShimmer * sweep * rimEdge;
      color += irid * glint * 1.2;
    }

    // T8.5 — depth banding: surface koi read brighter + clearer; deep koi
    // dim and shift bluer toward the water tone. (Distance fog below is
    // separate — this is purely about how deep the koi is sitting.)
    // KOI-VISIBILITY RECOMPOSE: raised the deep-end floors so a koi at the
    // bottom of its band still reads as a fish, not a near-black smudge —
    // dim floor 0.5→0.72, blue-shift floor 0.58→0.74 R (less red crush).
    float depth01 = clamp((vWorldPos.y - uDepthDeep) / (uDepthShallow - uDepthDeep), 0.0, 1.0);
    color *= mix(0.82, 1.14, depth01);
    color = mix(color * vec3(0.80, 0.88, 1.0), color, depth01);

    color *= uMoonDim; // T10.3 — koi go slightly darker when a cloud dims the moon

    ${DEPTH_FOG_APPLY_SNIPPET}
    gl_FragColor = vec4(color, 1.0);
  }
`;

export interface KoiMaterialOptions {
  baseKey: PaletteKey;
  patternKey?: PaletteKey;
  patternStrength?: number;
  rimKey?: PaletteKey;
  groundCool?: number; // 0 = warm moonlit (ogon), 1 = cool bone-china white
  swim?: SwimParams; // T8.3 swim params (phase/speed per koi); omit for defaults
  sss?: boolean; // T12.5 — false disables subsurface scattering (mobile)
  moonDir: Vector3;
  fogUniforms: { uFogColor: { value: Color }; uFogDensity: { value: number } };
}

export function makeKoiMaterialV3(opts: KoiMaterialOptions) {
  const uniforms = {
    uMoonDir: { value: opts.moonDir.clone().normalize() },
    uMoonColor: { value: new Color(palette.moonlight.hex) },
    uBaseColor: { value: new Color(palette[opts.baseKey].hex) },
    uPatternColor: { value: new Color(palette[opts.patternKey ?? 'koiPattern'].hex) },
    uPatternStrength: { value: opts.patternStrength ?? 0 },
    uRimColor: { value: new Color(palette[opts.rimKey ?? 'rimLight'].hex) },
    uGroundCool: { value: opts.groundCool ?? 0 },
    uDepthShallow: { value: 0.45 },
    uDepthDeep: { value: -1.8 },
    uMoonDim: { value: 1 },
    uSSS: { value: opts.sss === false ? 0 : 1 },
    uShimmer: { value: 0 },
    uTime: { value: 0 },
    ...makeSwimUniforms(opts.swim),
    uFogColor: opts.fogUniforms.uFogColor,
    uFogDensity: opts.fogUniforms.uFogDensity,
  };
  const material = new ShaderMaterial({
    vertexShader: KOI_VERT,
    fragmentShader: KOI_FRAG,
    uniforms,
    side: DoubleSide,
  });
  return { material, uniforms };
}

// Per-variant presets matching the three koi in the pond + the palette
// roles. Kohaku/sanke get a cooled bone-china ground (groundCool) + a
// cool rim so the white-vs-red contrast that defines those varieties
// stays crisp. Ogon keeps the warm moonlit gold with a warm rim.
export const KOI_VARIANTS: Record<
  string,
  {
    baseKey: PaletteKey;
    patternKey: PaletteKey;
    patternStrength: number;
    rimKey: PaletteKey;
    groundCool: number;
  }
> = {
  kohaku: { baseKey: 'koiPearl', patternKey: 'koiPattern', patternStrength: 1.0, rimKey: 'rimLight', groundCool: 1.0 },
  sanke: { baseKey: 'koiPearl', patternKey: 'koiPattern', patternStrength: 0.95, rimKey: 'rimLight', groundCool: 0.85 },
  ogon: { baseKey: 'koiGold', patternKey: 'koiGold', patternStrength: 0.0, rimKey: 'moonHalo', groundCool: 0.0 },
};
